import uniqueId from 'lodash/uniqueId'
import { StrictMode, useRef } from 'react'
import type { VFC } from 'react'
import { storyfirstParentUnit, storyfirstUnits } from '@opallabs/concepts'
import { Tooltip } from '@opallabs/kuiper'
import {
  formatCurrencyAmount,
  isNonNegativeNumber,
  isPositiveNumber,
} from '@opallabs/lambda'
import { BudgetHeader, formatBudget } from '../BudgetHeader'
import { BudgetMeter } from '../BudgetMeter'
import * as styles from './styles'
import type { AvailableBudgetProps, BudgetAllocationProps } from './types'

const remainingBudget = ({
  max,
  value,
}: Pick<AvailableBudgetProps, 'max' | 'value'>): number | undefined => {
  if (!isPositiveNumber(max)) return
  if (!isNonNegativeNumber(value)) return

  return max - value
}

export const remainingBudgetText = ({
  currency,
  max,
  value,
}: Omit<AvailableBudgetProps, 'budgetType' | 'context'>): string => {
  const remaining = remainingBudget({
    max,
    value,
  })
  if (remaining === undefined) return '—'

  if (remaining < 0)
    return `(${formatBudget(Math.abs(remaining), currency)} over)`
  if (remaining === 0) {
    return (
      formatCurrencyAmount({
        currency,
        value: remaining,
      }) + ' left'
    )
  }

  return `${formatBudget(remaining, currency)} left`
}

export const remainingBudgetTooltipText = ({
  context,
  currency,
  max,
  value,
}: Omit<AvailableBudgetProps, 'budgetType'>): string => {
  const remaining = remainingBudget({
    max,
    value,
  })

  if (remaining === undefined) {
    return storyfirstUnits`No ${context} budget set`
  }

  const remainingFormattedValue = formatCurrencyAmount({
    currency,
    value: Math.abs(remaining),
  })

  if (remaining < 0) {
    const parentContext = storyfirstParentUnit(context) || context
    return storyfirstUnits`${remainingFormattedValue} over ${parentContext} budget`
  }

  return storyfirstUnits`${remainingFormattedValue} of ${context} budget remaining`
}

/**
 * Displays how much money has been allocated to `budgetType` from `context`’s
 * total budget. If `context` is a story, `budgetType` may be moments (all
 * allocations for moments in the story) or content (all allocations for
 * content in the story). If `context` is a moment, `budgetType` will be
 * content (all allocations for content in the moment).
 *
 * `value` is the amount that’s allocated, and may be larger than `max`. The UI
 * also shows the amount that’s still available to allocate.
 */
export const BudgetAllocation: VFC<BudgetAllocationProps> = ({
  budgetType,
  context,
  currency = 'USD',
  htmlId,
  max = 0,
  styleOverrides = {},
  theme = 'light',
  value = 0,
}) => {
  const headerTitle = storyfirstUnits`${budgetType} Allocation`
  const meterLabelId = useRef(uniqueId('photon-budget-allocation'))

  return (
    <StrictMode>
      <div
        css={[styles.container({ theme }), styleOverrides]}
        id={htmlId}
        role="group"
      >
        <BudgetHeader
          ariaHidden
          htmlId={meterLabelId.current}
          max={value}
          title={headerTitle}
          {...{
            budgetType,
            context,
            currency,
            theme,
          }}
        />

        <div css={styles.content}>
          <BudgetMeter
            ariaLabelledBy={meterLabelId.current}
            styleOverrides={styles.meter}
            // Currently the dark theme is unusable on the moment shroud
            // sidebar.
            theme="light"
            {...{
              currency,
              max,
              value,
            }}
          />
          <Tooltip
            position="top"
            content={
              <span css={styles.tooltipText}>
                {remainingBudgetTooltipText({
                  context: budgetType,
                  currency,
                  max,
                  value,
                })}
              </span>
            }
            theme={theme}
            trigger={
              <span aria-hidden css={styles.text}>
                {remainingBudgetText({
                  currency,
                  max,
                  value,
                })}
              </span>
            }
          />
        </div>
      </div>
    </StrictMode>
  )
}
BudgetAllocation.displayName = 'Photon: BudgetAllocation'
